import React, { createRef } from 'react';
import animateScrollTo from 'animated-scroll-to';

import * as Types from 'types';
import { User } from 'components/users/User';
import { PaginatedGrid } from 'components/PaginatedGrid';
import { RadioGroup } from 'components/UI/RadioGroup';
import { NoResults } from 'apps/browse/components/NoResults';
import { BrowseHeadline } from 'apps/browse/components/BrowseHeadline';
import { BrowseSidebar } from 'apps/browse/components/BrowseSidebar';

import { useNumericQueryParam, useEnumQueryParam } from 'hooks/useQueryParam';
import { useUpdateQueryParams } from 'hooks/useUpdateQueryParams';
import { useDebounce } from 'hooks/useDebounce';
import { useModalState } from 'hooks/useModalState';

import { usePaginatedResource } from 'hooks/usePaginatedResource';

import { mapper, PER_PAGE } from './helpers';
import { BrowseFilters } from './types';

export const BrowseArtists: React.FC = () => {
  const { updateQueryParams } = useUpdateQueryParams();

  const order = useEnumQueryParam('order', BrowseFilters.ORDER_TYPES, 'date');
  const page = useNumericQueryParam('page', 1);
  const type = useEnumQueryParam('type', Types.ARTIST_TYPES, 'curated');

  const url = useDebounce(
    mapper('/api/internal/users/lists', {
      type,
      order,
    }),
    50,
  );

  const { isModalOpen, openModal, closeModal } = useModalState();

  const { items, isFetching, count } = usePaginatedResource(url, page, PER_PAGE);

  const contentRef = createRef<HTMLDivElement>();

  return (
    <div>
      <BrowseHeadline title='Artists' buttonTitle='Filter' onClick={openModal} />
      <div className='row'>
        <BrowseSidebar isModalMode={isModalOpen} title='Artists' onRequestClose={closeModal}>
          <RadioGroup
            className='mb-2'
            name='artist-type'
            label='Section'
            options={BrowseFilters.LABELED_ARTIST_TYPES}
            selected={type}
            onChange={(itemId) => {
              updateQueryParams({ type: itemId, page: '1' });
            }}
          />
          <RadioGroup
            className='mb-2'
            name='order'
            label='Order By'
            options={BrowseFilters.LABELED_ORDER_TYPES}
            selected={order}
            onChange={(itemId) => {
              updateQueryParams({ order: itemId });
            }}
          />
        </BrowseSidebar>
        <div className='col' ref={contentRef}>
          {!isFetching && items.length === 0 ? <NoResults /> : null}
          <PaginatedGrid
            currentPage={page}
            items={items}
            count={Math.ceil(count / PER_PAGE)}
            renderItem={(item, index) => (
              <User
                className='col-12 col-md-6 col-std-4 mb-2'
                key={index}
                userId={item.id}
                cacheKey={item.cacheKey}
              />
            )}
            onPageChange={(page) => {
              updateQueryParams({ page: String(page + 1) });
              if (contentRef.current) {
                const elementTop = contentRef.current.getBoundingClientRect().top;

                if (elementTop + window.scrollY < window.scrollY) {
                  animateScrollTo(elementTop + window.scrollY, {
                    verticalOffset: -32,
                  });
                }
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};
